import React, { useState, useCallback, useRef, useEffect } from 'react';
import './VocabularyPractice.css';
import api, { BASE_URL } from './api';
import Login from './Login';

// 引入富文本解析库，假设项目中已安装
const parse = (html) => {
    if (!html) return <></>;
    return (
      <div dangerouslySetInnerHTML={{ __html: html }} />
    );
  }

const CategoryTest = () => {
  // 状态管理
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [vocabList, setVocabList] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [feedback, setFeedback] = useState('');
  const [isCorrect, setIsCorrect] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [testMode, setTestMode] = useState(false);
  const [testCompleted, setTestCompleted] = useState(false);
  const [testResults, setTestResults] = useState({
    correct: 0,
    incorrect: 0,
    wrongAnswers: []
  });
  const [isLogin, setIsLogin] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const audioRef = useRef(null);
  // 测试数量相关状态
  const [testSize, setTestSize] = useState(10);
  const [availableTestSizes, setAvailableTestSizes] = useState([10]);
  const [testVocabList, setTestVocabList] = useState([]);
  // Remark显示状态
  const [showRemark, setShowRemark] = useState(false);
  // 复习相关状态
  const [vocabStats, setVocabStats] = useState([]);
  const [reviewParams, setReviewParams] = useState({
    minErrorRate: 0.3, // 最小错误率，高于此值的单词需要优先复习
    maxInterval: 30,   // 最大复习间隔（天）
    difficultyWeight: 0.5 // 难度系数权重
  });
  
  // 显示英文状态 - 添加用于点击中文显示英文的功能
  const [showEnglish, setShowEnglish] = useState(false);


  // 自定义日志函数
  const logDebug = useCallback((message, data = null) => {
    const timestamp = new Date().toLocaleTimeString();
    console.log(`[${timestamp}] ${message}`, data);
  }, []);

  // 检查登录状态
  const checkLoginStatus = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      try {
        const userInfo = await api.getUserInfo();
        setCurrentUser(userInfo);
        setIsLogin(true);
        logDebug('用户已登录', userInfo);
      } catch (err) {
        logDebug('获取用户信息失败', err.message);
        api.logout();
        setIsLogin(false);
        setCurrentUser(null);
      }
    } else {
      setIsLogin(false);
      setCurrentUser(null);
    }
  }, [logDebug]);

  // 初始检查登录状态
  useEffect(() => {
    checkLoginStatus();
  }, [checkLoginStatus]);

  // 获取分类数据
  const fetchCategories = useCallback(async () => {
    if (!isLogin) return;

    setLoading(true);
    setError('');
    try {
      logDebug('开始请求分类数据');
      const response = await api.fetchWithAuth('/api/sentences/categories/?type=vocabulary', {
        method: 'GET'
      });
      logDebug('分类API响应状态', response.status);

      if (!response.ok) {
        throw new Error(`分类API请求失败，状态码: ${response.status}`);
      }

      const data = await response.json();
      logDebug('分类数据', data);
      const categories = Array.isArray(data) ? data : (data.results || []);
      setCategories(categories);
      logDebug('分类加载完成', { 数量: categories.length });

      // 尝试自动选择"what is this call"分类
      const whatIsThisCallCat = categories.find(cat => cat.name.toLowerCase().includes('what is this call'));
      if (whatIsThisCallCat) {
        setSelectedCategory(whatIsThisCallCat.id);
      }
    } catch (err) {
      logDebug('分类加载失败', err.message);
      setError(`分类加载失败: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [isLogin, logDebug]);

  // 加载分类词汇
  const loadVocabByCategory = useCallback(async () => {
    if (!selectedCategory || !isLogin) return;

    setLoading(true);
    setError('');
    try {
      logDebug('开始加载分类词汇', { categoryId: selectedCategory });
      const params = new URLSearchParams({
        categories__id: selectedCategory,
        page_size: 10000
      });
      const vocab = await api.getVocabulary(params.toString());
      setVocabList(vocab);
      setCurrentIndex(0);
      setTestCompleted(false);
      setTestMode(false);
      setTestResults({
        correct: 0,
        incorrect: 0,
        wrongAnswers: []
      });
      logDebug('分类词汇加载完成', { 数量: vocab.length });
      if (vocab.length > 0) {
        logDebug('第一个词汇的完整数据', vocab[0]);
      }
      vocab.forEach(item => {
        logDebug('词汇图片URL', { word: item.chinese, imageUrl: item.image_url });
      });
    } catch (err) {
      logDebug('分类词汇加载失败', err.message);
      setError(`词汇加载失败: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, isLogin, logDebug]);

  // 登录成功后的处理
  const handleLogin = (userData) => {
    setCurrentUser(userData);
    setIsLogin(true);
    logDebug('登录成功', userData);
    fetchCategories();
    // 登录成功后获取单词统计数据
    fetchVocabStats();
  };

  // 登出处理
  const handleLogout = () => {
    api.logout();
    setIsLogin(false);
    setCurrentUser(null);
    setVocabList([]);
    setCategories([]);
    logDebug('用户已登出');
  };

  // 从后端获取单词统计
  const fetchVocabStats = useCallback(async () => {
    logDebug('开始获取单词统计');
    try {
      const stats = await api.getVocabStats();
      setVocabStats(stats);
      logDebug('单词统计加载完成', { 总记录数: stats.length });
    } catch (err) {
      logDebug('单词统计加载失败', err.message);
    }
  }, [logDebug, setVocabStats]);

  // 上报测试结果到后端
  const reportTestResult = useCallback(async (vocabId, errorCount) => {
    logDebug('上报测试结果', { vocabId, errorCount });

    try {
      const data = await api.reportTestResult(vocabId, errorCount);
      logDebug('测试结果上报成功', data);
      // 刷新单词统计数据
      fetchVocabStats();
    } catch (err) {
      logDebug('测试结果上报失败', err.message);
      if (!err.message.includes('500')) {
        setError(`上报测试结果失败: ${err.message}`);
      }
    }
  }, [logDebug, fetchVocabStats, setError]);

  // 检查答案
  const checkAnswer = useCallback(() => {
    if (testMode ? !testVocabList.length : !vocabList.length) return;

    const current = testMode ? testVocabList[currentIndex] : vocabList[currentIndex];
    const isMatch = userInput.trim().toLowerCase() === current.english.toLowerCase();
    setIsCorrect(isMatch);
    setFeedback(isMatch ? '正确！' : `错误，正确答案：${current.english}`);

    // 上报测试结果到后端
    const errorCount = isMatch ? 0 : 1;
    reportTestResult(current.id, errorCount);

    // 更新测试结果
    setTestResults(prev => ({
      correct: prev.correct + (isMatch ? 1 : 0),
      incorrect: prev.incorrect + (isMatch ? 0 : 1),
      wrongAnswers: isMatch ? prev.wrongAnswers : [...prev.wrongAnswers, {
        chinese: current.chinese,
        correctEnglish: current.english,
        userAnswer: userInput,
        imageUrl: current.image_url
      }]
    }));

    // 检查是否完成测试
    if (currentIndex >= testVocabList.length - 1) {
      setTimeout(() => {
        setTestCompleted(true);
      }, 1000);
    }
  }, [
    testMode,
    testVocabList,
    currentIndex,
    userInput,
    vocabList,
    setIsCorrect,
    setFeedback,
    setTestResults,
    setTestCompleted,
    reportTestResult
  ]);

  // 重置输入状态 - 移到前面并使用useCallback包装
  const resetInput = useCallback(() => {
    setUserInput('');
    setFeedback('');
    setIsCorrect(null);
  }, [setUserInput, setFeedback, setIsCorrect]);

  // 导航控制
  const nextVocab = useCallback(() => {
    if (testMode) {
      if (testVocabList.length && currentIndex < testVocabList.length - 1) {
        setCurrentIndex(prev => prev + 1);
        resetInput();
      }
    } else {
      if (vocabList.length) {
        // 在非测试模式下支持循环浏览
        setCurrentIndex(prev => (prev + 1) % vocabList.length);
        resetInput();
      }
    }
  }, [testMode, testVocabList.length, currentIndex, vocabList.length, resetInput]);

  const prevVocab = useCallback(() => {
    if (testMode) {
      if (testVocabList.length && currentIndex > 0) {
        setCurrentIndex(prev => prev - 1);
        resetInput();
      }
    } else {
      if (vocabList.length) {
        // 在非测试模式下支持循环浏览
        setCurrentIndex(prev => (prev - 1 + vocabList.length) % vocabList.length);
        resetInput();
      }
    }
  }, [testMode, testVocabList.length, currentIndex, vocabList.length, resetInput]);

  // 开始测试
  const startTest = () => {
    if (vocabList.length === 0) return;

    // 从词汇列表中随机选择指定数量的词汇
    const shuffled = [...vocabList].sort(() => 0.5 - Math.random());
    const selected = shuffled.slice(0, testSize);
    
    // 添加调试信息，检查选中的词汇是否包含pronaunciation_url
    if (selected.length > 0) {
      const hasPronunciation = selected.some(vocab => vocab.pronunciation_url);
      logDebug('测试词汇准备完成', {
        totalSelected: selected.length,
        hasPronunciation,
        samplePronunciationUrl: selected[0]?.pronunciation_url || '无'
      });
    }

    setTestVocabList(selected);
    setTestMode(true);
    setCurrentIndex(0);
    setTestResults({
      correct: 0,
      incorrect: 0,
      wrongAnswers: []
    });
    setTestCompleted(false);
    resetInput();
    logDebug('开始测试');
  };

  // 重置测试
  const resetTest = () => {
    setTestMode(false);
    setTestCompleted(false);
    setCurrentIndex(0);
    resetInput();
    logDebug('重置测试');
  };

  // 统一的音频播放函数 - Windows版本实现方式
  const playAudio = () => {
    if (testMode ? !testVocabList.length : !vocabList.length) return;

    const current = testMode ? testVocabList[currentIndex] : vocabList[currentIndex];
    
    if (current.pronunciation_url) {
      // 停止之前可能在播放的音频
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }

      audioRef.current = new Audio(current.pronunciation_url);
      audioRef.current.play()
        .then(() => {
          setAudioPlaying(true);
          logDebug('音频播放开始', { url: current.pronunciation_url });
        })
        .catch(err => {
          logDebug('音频播放失败', err.message);
          setError(`音频播放失败: ${err.message}`);
          setAudioPlaying(false);
        });

      audioRef.current.onended = () => {
        setAudioPlaying(false);
        logDebug('音频播放结束');
      };
    } else {
      setError('当前词汇暂无发音文件');
    }
  };





  // 渲染媒体文件（图片或视频）
  const renderMedia = (mediaUrl, altText) => {
    // 如果mediaUrl为空，直接返回
    if (!mediaUrl) {
      logDebug('媒体URL为空');
      return null;
    }

    logDebug('原始媒体URL:', mediaUrl);

    // 清理URL，移除可能存在的反引号或其他特殊字符
    let cleanMediaUrl = mediaUrl;
    if (typeof cleanMediaUrl === 'string') {
      cleanMediaUrl = cleanMediaUrl.replace(/`/g, ''); // 移除所有反引号
      cleanMediaUrl = cleanMediaUrl.trim(); // 移除首尾空格
    }
    
    // 根据当前环境调整媒体文件URL
    let adjustedUrl = cleanMediaUrl;
    
    // 检查是否已经是完整URL
    if (cleanMediaUrl.startsWith('http://') || cleanMediaUrl.startsWith('https://')) {
      // 已经是完整URL，直接使用
      logDebug('使用完整URL:', adjustedUrl);
    } else {
      // 如果是相对路径，确保以/开头
      if (!cleanMediaUrl.startsWith('/')) {
        adjustedUrl = '/' + cleanMediaUrl;
        logDebug('添加前缀后URL:', adjustedUrl);
      }
      
      // 确保URL以/media/开头
      if (!adjustedUrl.startsWith('/media/')) {
        if (adjustedUrl.includes('/media/')) {
          // 提取media/及其后面的部分
          const mediaIndex = adjustedUrl.indexOf('/media/');
          adjustedUrl = adjustedUrl.substring(mediaIndex);
        } else if (adjustedUrl.includes('media/')) {
          // 如果URL中包含media/但前面没有/
          const mediaIndex = adjustedUrl.indexOf('media/');
          adjustedUrl = '/' + adjustedUrl.substring(mediaIndex);
        } else {
          // 否则，添加/media/前缀
          adjustedUrl = '/media/' + (adjustedUrl.startsWith('/') ? adjustedUrl.substring(1) : adjustedUrl);
        }
        logDebug('适应环境后的URL:', adjustedUrl);
      }
      
      // 为阿里云环境构建完整URL
      const baseUrl = window.location.origin;
      adjustedUrl = baseUrl + adjustedUrl;
      logDebug('在阿里云环境中使用完整URL:', adjustedUrl);
    }

    // 根据文件扩展名判断是图片还是视频
    const fileExtension = adjustedUrl.split('.').pop()?.toLowerCase() || '';
    const videoExtensions = ['mp4', 'webm', 'ogg'];
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'];

    // 检查视频文件格式是否受支持
    const supportedVideoFormats = ['mp4', 'webm', 'ogg'];
    
    if (videoExtensions.includes(fileExtension)) {
      // 视频文件
      if (!supportedVideoFormats.includes(fileExtension)) {
        logDebug('不支持的视频格式:', fileExtension);
        return <div style={{ color: '#999', fontStyle: 'italic' }}>不支持的视频格式</div>;
      }
      
      // 新增：打印视频词汇的发音文件状态
      const currentVocab = testMode ? testVocabList[currentIndex] : vocabList[currentIndex];
      logDebug('视频类型词汇发音文件检查', {
        vocabId: currentVocab.id,
        chinese: currentVocab.chinese,
        videoUrl: adjustedUrl,
        hasPronunciation: !!currentVocab.pronunciation_url // 记录是否有发音文件
      });
      

      
      return (
        <video
          src={adjustedUrl}
          alt={altText || '词汇视频'}
          controls
          style={{ maxWidth: '300px', maxHeight: '300px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
          onError={(e) => {
            logDebug('视频加载失败', { requestPath: adjustedUrl, originalUrl: cleanMediaUrl });
            // 简单记录错误，不隐藏视频元素，让浏览器的默认错误处理显示
          }}
          onCanPlay={(e) => {
            logDebug('视频可以播放', { url: adjustedUrl });
          }}
        />
      );
    } else if (imageExtensions.includes(fileExtension)) {
      // 图片文件
      return (
        <img
          src={adjustedUrl}
          alt={altText || '词汇图片'}
          style={{ maxWidth: '300px', maxHeight: '300px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
          onError={(e) => {
            console.error('媒体加载失败，请求路径:', adjustedUrl);
            console.error('媒体文件名:', cleanMediaUrl);
            // 设置默认图片
            e.target.onerror = null;
            e.target.src = `${BASE_URL}/placeholder-image.png`;
          }}
        />
      );
    }

    // 不支持的媒体类型
    return <div style={{ color: '#999', fontStyle: 'italic' }}>不支持的文件类型</div>;
  };



  // 组件挂载时加载分类和单词统计
  useEffect(() => {
    if (isLogin) {
      fetchCategories();
      fetchVocabStats();
    }
  }, [isLogin, fetchCategories, fetchVocabStats]);

  // 添加键盘事件支持
  useEffect(() => {
    const handleKeyDown = (e) => {
      // 只有在非测试模式下或测试模式但未完成时允许键盘导航
      if ((!testMode || (testMode && !testCompleted)) && 
          // 避免在输入框聚焦时触发
          e.target.tagName.toLowerCase() !== 'input') {
        switch (e.key) {
          case 'ArrowRight':
            nextVocab();
            break;
          case 'ArrowLeft':
            prevVocab();
            break;
          default:
            // Do nothing for other keys
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [prevVocab, nextVocab, testMode, testCompleted]);

  // 根据分类更新可用测试数量
  useEffect(() => {
    if (vocabList.length === 0) {
      setAvailableTestSizes([10]);
      setTestSize(10);
      return;
    }

    // 生成可用的测试数量选项
    const sizes = [10, 20, 30, 40, 50].filter(size => size <= vocabList.length);
    setAvailableTestSizes(sizes.length > 0 ? sizes : [Math.min(10, vocabList.length)]);

    // 如果当前testSize不在新的可用选项中，设置为最小可用值
    if (!sizes.includes(testSize)) {
      setTestSize(sizes.length > 0 ? sizes[0] : Math.min(10, vocabList.length));
    }
  }, [selectedCategory, vocabList, testSize]);

  // 分类变化时加载词汇
  useEffect(() => {
    if (selectedCategory) {
      loadVocabByCategory();
    }
  }, [selectedCategory, loadVocabByCategory]);

  // 组件卸载时停止音频
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // 未登录状态
  if (!isLogin) {
    return (
      <div className="login-container">
        <h2>请先登录</h2>
        <Login onLogin={handleLogin} />
      </div>
    );
  }

  // 加载状态
  if (loading) return <div className="loading">加载中...</div>;

  // 错误状态
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="container">
      <div className="user-info">
        <span>欢迎，{currentUser.username}</span>
        <button onClick={handleLogout} className="logout-button">登出</button>
      </div>
      <h2>语音图片测试</h2>

      {/* 分类选择 */}
      <div className="category-selector" style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#f0f0f0', borderRadius: '8px' }}>
            <label style={{ marginRight: '10px', fontWeight: 'bold' }}>选择分类：</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              disabled={loading || testMode}
              style={{ padding: '8px 15px', border: '1px solid #ddd', borderRadius: '4px', minWidth: '200px' }}
            >
              <option value="">请选择分类</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
            <span className="count-info" style={{ marginLeft: '10px', color: '#666' }}>
              共 {vocabList.length} 条
            </span>
          </div>

          {/* 测试数量选择 */}
          {!testMode && (
            <div className="test-settings" style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
              <label style={{ marginRight: '10px', fontWeight: 'bold' }}>选择测试数量：</label>
              <select
                value={testSize}
                onChange={(e) => setTestSize(parseInt(e.target.value))}
                disabled={loading || vocabList.length === 0}
                style={{ padding: '8px 15px', border: '1px solid #ddd', borderRadius: '4px', minWidth: '100px' }}
              >
                {availableTestSizes.map(size => (
                  <option key={size} value={size}>{size} 个</option>
                ))}
              </select>
              <button
                onClick={startTest}
                disabled={loading || vocabList.length === 0}
                style={{ marginLeft: '15px', padding: '8px 15px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              >
                开始测试
              </button>
            </div>
          )}

      {/* 测试进行中提示 */}
      {testMode && !testCompleted && (
        <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#e3f2fd', borderRadius: '8px' }}>
          <p style={{ margin: 0, fontWeight: 'bold' }}>测试进行中: {currentIndex + 1}/{testVocabList.length}</p>
          <button
            onClick={resetTest}
            style={{ marginTop: '5px', padding: '5px 10px', backgroundColor: '#f44336', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
          >
            取消测试
          </button>
        </div>
      )}

      {/* 词汇展示 */}
      {(testMode ? testVocabList.length : vocabList.length) > 0 && (
        <div className="vocab-card">
          <h3>What is this called in English?</h3>
          
          {/* 中文意思展示 - 添加点击显示英文功能 */}
          <div 
            className="chinese-meaning" 
            style={{ 
              margin: '10px 0', 
              padding: '10px', 
              backgroundColor: '#f8f9fa', 
              borderRadius: '4px', 
              fontSize: '18px', 
              fontWeight: 'bold', 
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
            onClick={() => setShowEnglish(!showEnglish)}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9ecef'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
          >
            {testMode ? testVocabList[currentIndex].chinese : vocabList[currentIndex].chinese}
            {showEnglish && (
              <div style={{ 
                marginTop: '8px', 
                padding: '8px', 
                backgroundColor: '#e3f2fd', 
                borderRadius: '4px', 
                fontSize: '16px', 
                fontWeight: 'normal',
                color: '#1976d2'
              }}>
                {testMode ? testVocabList[currentIndex].english : vocabList[currentIndex].english}
              </div>
            )}
          </div>

          {/* 媒体展示 (图片或视频) */}
          <div className="image-container" style={{ margin: '20px 0', textAlign: 'center', minHeight: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {(testMode ? testVocabList[currentIndex].image_url : vocabList[currentIndex].image_url) ? (
              renderMedia(testMode ? testVocabList[currentIndex].image_url : vocabList[currentIndex].image_url, testMode ? testVocabList[currentIndex].chinese : vocabList[currentIndex].chinese)
            ) : (
              <div style={{ color: '#999', fontStyle: 'italic' }}>无媒体文件可用</div>
            )}
          </div>

          {/* 按钮容器 - 播放发音和查看解释按钮放在一起并居中显示 */}
          <div style={{ display: 'flex', gap: '15px', marginBottom: '15px', alignItems: 'center', justifyContent: 'center' }}>
            {(() => {
              const currentVocab = testMode ? testVocabList[currentIndex] : vocabList[currentIndex];
              const hasPronunciation = !!currentVocab.pronunciation_url; // 判断是否有发音文件
              const mediaUrl = currentVocab.image_url;
              const isVideo = mediaUrl && (mediaUrl.endsWith('.mp4') || mediaUrl.includes('.mp4?'));
                
              // 仅在控制台输出，不显示在UI上
              console.log('音频按钮状态检查:', {
                hasPronunciation,
                mediaType: isVideo ? 'video' : 'image',
                currentMode: testMode ? 'test' : 'study',
                mediaUrl
              });
                
              return (
                <button
                  onClick={playAudio}
                  disabled={!hasPronunciation || audioPlaying} // 无发音文件时禁用
                  style={{
                    padding: '8px 15px',
                    width: '120px',
                    backgroundColor: !hasPronunciation ? '#e0e0e0' : (audioPlaying ? '#cccccc' : '#2196F3'),
                    color: !hasPronunciation ? '#999' : 'white', // 无发音文件时灰色文字
                    border: 'none',
                    borderRadius: '4px',
                    cursor: !hasPronunciation ? 'not-allowed' : (audioPlaying ? 'not-allowed' : 'pointer'),
                    opacity: !hasPronunciation ? 0.8 : 1 // 无发音文件时降低透明度
                  }}
                >
                  {/* 按钮文字根据是否有发音文件动态变化 */}
                  {hasPronunciation ? (audioPlaying ? '播放中...' : '播放发音') : '无发音文件'}
                </button>
              );
            })()}
            
            {/* 查看Remark按钮 */}
            <button
              onClick={() => setShowRemark(!showRemark)}
              style={{
                width: '120px',
                padding: '8px 15px',
                backgroundColor: '#2196F3',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {showRemark ? '隐藏解释' : '查看解释'}
            </button>
          </div>

          {/* Remark内容显示区域 */}
          {showRemark && (
            <div
              className="remark-content"
              style={{
                marginBottom: '20px',
                padding: '15px',
                backgroundColor: '#f9f9f9',
                borderRadius: '8px',
                border: '1px solid #ddd',
                maxHeight: '300px',
                overflowY: 'auto'
              }}
            >
              {parse((testMode ? testVocabList[currentIndex].remark : vocabList[currentIndex].remark) || '')}
            </div>
          )}

          {/* 输入区域 */}
          {testMode && !testCompleted && (
            <div className="input-area">
              <input
                type="text" 
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                placeholder="请输入英文..."
                autoFocus
                style={{ marginRight: '10px', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
              />
              <button 
                onClick={checkAnswer}
                style={{ padding: '8px 15px', backgroundColor: '#2196F3', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              >
                确认
              </button>
              <small style={{ marginLeft: '10px', color: '#666' }}>忘记了？点击上方中文显示英文</small>
            </div>
          )}
        </div>
      )}

      {/* 反馈区域 - 关键修复：确保此区域在正确的层级 */}
      {feedback && (
        <div className={`feedback ${isCorrect ? 'correct' : 'wrong'}`}>
          {/* 反馈 */}
          {feedback}
        </div>
      )}

      {/* 导航按钮 */}
      {vocabList.length > 1 && (
        <div className="nav-buttons">
          <button onClick={prevVocab}>上一个</button>
          <button onClick={nextVocab}>下一个</button>
        </div>
      )}

      {/* 测试结果区域 */}
      {testCompleted && (
        <div className="test-results" style={{ marginTop: '30px', padding: '20px', backgroundColor: '#f0f0f0', borderRadius: '8px', border: '2px solid #4CAF50' }}>
          <h3 style={{ marginTop: 0, color: '#333' }}>测试完成！</h3>
          <div style={{ marginBottom: '15px' }}>
            <p style={{ fontSize: '18px', fontWeight: 'bold' }}>正确率: {Math.round((testResults.correct / Math.max(testResults.correct + testResults.incorrect, 1)) * 100)}%</p>
            <p>正确: {testResults.correct} / 错误: {testResults.incorrect}</p>
          </div>

          {testResults.wrongAnswers.length > 0 && (
            <div>
              <h4 style={{ color: '#d32f2f' }}>错误内容:</h4>
              <ul style={{ listStyleType: 'none', padding: 0 }}>
                {testResults.wrongAnswers.map((item, index) => (
                  <li key={index} style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#fff', borderRadius: '4px', borderLeft: '4px solid #d32f2f' }}>

                    <p><strong>中文:</strong> {item.chinese}</p>
                    <p><strong>正确答案:</strong> {item.correctEnglish}</p>
                    <p><strong>你的答案:</strong> {item.userAnswer}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
            <button
              onClick={resetTest}
              style={{ padding: '10px 15px', backgroundColor: '#2196F3', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
            >
              返回练习
            </button>
            <button
              onClick={startTest}
              style={{ padding: '10px 15px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
            >
              再次测试
            </button>
          </div>
        </div>
      )}

      {/* 复习建议区域 */}
          {!testMode && (
            <div className="review-section">
              <h4>复习建议</h4>
              {vocabStats.length === 0 ? (
                <p>暂无复习数据，请先进行测试</p>
              ) : (
                <div className="review-container">
                  {/* 过滤和排序逻辑优化 */}
                  {(() => {
                    // 过滤需要复习的单词
                    const reviewableVocabs = vocabStats.filter(stat => {
                      // 检查是否需要复习（下次复习日期 <= 今天且错误率 >= 最小阈值）
                      const shouldReview = new Date(stat.next_review_date) <= new Date() && 
                                          stat.error_rate >= reviewParams.minErrorRate;
                       
                      if (!selectedCategory) {
                        // 未选择分类，返回所有需要复习的单词
                        return shouldReview;
                      }
                       
                      // 检查分类匹配 - 优先匹配category_ids数组
                      // 注意：根据后端返回字段实际情况调整这里的字段名
                      const isCategoryMatch = 
                        // 检查是否有category_ids数组且包含当前选中分类
                        (stat.category_ids && Array.isArray(stat.category_ids) && 
                         stat.category_ids.some(catId => String(catId) === String(selectedCategory))) ||
                        // 兼容旧字段：检查vocabulary_categories
                        (stat.vocabulary_categories && stat.vocabulary_categories.includes(selectedCategory)) ||
                        // 兼容旧字段：检查categories
                        (stat.categories && stat.categories.includes(selectedCategory)) ||
                        // 兼容旧字段：检查单个category_id
                        (stat.category_id && String(stat.category_id) === String(selectedCategory));
                       
                      return shouldReview && isCategoryMatch;
                    });
                    
                    // 按难度降序，再按错误率降序排序
                    const sortedVocabs = reviewableVocabs.sort((a, b) => {
                      // 首先按难度降序
                      if (b.difficulty !== a.difficulty) {
                        return b.difficulty - a.difficulty;
                      }
                      // 难度相同时按错误率降序
                      return b.error_rate - a.error_rate;
                    }).slice(0, 10); // 只显示前10个
                    
                    return (
                      <>
                        <h5>需要复习的单词 ({reviewableVocabs.length})</h5>
                        {sortedVocabs.length > 0 ? (
                          sortedVocabs.map(stat => (
                            <div key={stat.id} className="review-item">
                              <span>{stat.vocabulary_chinese} ({stat.vocabulary_english}): </span>
                              <span className="review-info">
                                错误率: {stat.error_rate * 100}% | 下次复习: {stat.next_review_date}
                              </span>
                            </div>
                          ))
                        ) : (
                          // 空状态提示优化
                          <p style={{ color: '#666', fontStyle: 'italic' }}>
                            {selectedCategory ? '该分类下暂无需要复习的词汇' : '暂无需要复习的词汇'}
                          </p>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          )}

          {/* 参数设置区域 */}
          <div className="settings-section">
            <h4>复习参数设置</h4>
            <div className="settings-container">
              <div className="setting-item">
                <label>最小错误率阈值: </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={reviewParams.minErrorRate}
                  onChange={(e) => setReviewParams(prev => ({...prev, minErrorRate: parseFloat(e.target.value)}))}
                />
                <span>{reviewParams.minErrorRate}</span>
              </div>
              <div className="setting-item">
                <label>最大复习间隔(天): </label>
                <input
                  type="number"
                  min="1"
                  max="90"
                  value={reviewParams.maxInterval}
                  onChange={(e) => setReviewParams(prev => ({...prev, maxInterval: parseInt(e.target.value)}))}
                />
              </div>
              <div className="setting-item">
                <label>难度系数权重: </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={reviewParams.difficultyWeight}
                  onChange={(e) => setReviewParams(prev => ({...prev, difficultyWeight: parseFloat(e.target.value)}))}
                />
                <span>{reviewParams.difficultyWeight}</span>
              </div>
            </div>
          </div>

          {/* 无数据状态 */}
          {!testMode && vocabList.length === 0 && selectedCategory && (
            <div className="no-data">该分类下无词汇</div>
          )}
    </div>
  );
};

export default CategoryTest;
